import React, { useState } from 'react';

import Button from '@amzn/awsui-components-react/polaris/button';
import CopyToClipboard from '@amzn/awsui-components-react/polaris/copy-to-clipboard';
import Header from '@amzn/awsui-components-react/polaris/header';
import Input from '@amzn/awsui-components-react/polaris/input';
import Pagination from '@amzn/awsui-components-react/polaris/pagination';
import SpaceBetween from '@amzn/awsui-components-react/polaris/space-between';
import Table, { TableProps } from '@amzn/awsui-components-react/polaris/table';
import { Shift } from '@amzn/red-velvet-api';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useBulkParticipantSessionStorage } from '../../../hooks/attendance/bulkParticipantSessionStorage';
import {
  ParamsParticipantData,
  useParticipantSessionStorage,
} from '../../../hooks/attendance/participantSessionStorage';
import { useParticipants } from '../../../hooks/attendance/useParticipants';
import { useEditableEventInfo } from '../../../hooks/events/editableEventInfo';
import { usePaginationStrings } from '../../../hooks/localization/paginationStrings';
import { usePropertyFilterStrings } from '../../../hooks/localization/propertyFilterStrings';
import { useTableStrings } from '../../../hooks/localization/tableStrings';
import { calculateShiftTime } from '../../../hooks/shifts/shiftTime';
import { useTableColumns } from '../../../hooks/tableColumns';
import { useUser } from '../../../hooks/user';
import {
  Amazonian,
  getAliases,
  useAttendanceColumnMap,
  useEventLevelAttendanceColumnMap,
} from '../../../utils/attendance/peopleSearch';
import { getValueFromSearchParam } from '../../../utils/url';
import { publishKatalMetric } from '../../katalAnalytics';
import { TimeModal, parseTime } from '../../shifts/timeModal';
import { PeoplePreferences } from '../wizard/peoplePreferences';
import { TablePropertyFilter } from '../wizard/tablePropertyFilter';

export type AttendeeTableProps = {
  isImmutable?: boolean;
  shifts?: Shift[];
  vcCreated?: boolean;
} & Partial<TableProps>;

export function CoordinatorAttendeeTable(props: AttendeeTableProps) {
  const { isImmutable, shifts, vcCreated } = props;
  const namespaces = ['attendance', 'translation'];
  const { t } = useTranslation(namespaces);

  const [searchParams] = useSearchParams();
  const eventId = getValueFromSearchParam(searchParams, 'eventId', '');
  const shiftId = getValueFromSearchParam(searchParams, 'shiftId', '');

  const { userId } = useUser();

  const isEventLevelTracking = !!eventId && !shiftId;

  const [pageSize, setPageSize] = useState(100);

  const { items, participants, isLoading, collectionProps, paginationProps, propertyFilterProps } =
    useParticipants(namespaces, 'coordinatorAttendeeTable', eventId, shiftId, pageSize, shifts);

  const propertyFilterStrings = usePropertyFilterStrings('propertyFilter', namespaces);

  const { selectedItems = [] } = collectionProps;

  const tableStrings = useTableStrings(
    'coordinatorAttendeeTable',
    (item: Amazonian) => item.name,
    namespaces
  );

  // We need the shift information to determine the default length of time for attendance
  const { shift, isFetched } = useEditableEventInfo();
  const shiftMins = calculateShiftTime(shift, vcCreated);

  const paginationStrings = usePaginationStrings('coordinatorAttendeeTable', namespaces);
  const singleShiftColMap = useAttendanceColumnMap(shiftMins, isImmutable);
  const eventLevelColMap = useEventLevelAttendanceColumnMap(isImmutable, shifts, vcCreated);
  const colMap = isEventLevelTracking ? eventLevelColMap : singleShiftColMap;

  const columns = useTableColumns(
    colMap,
    'coordinatorAttendeeTable',
    namespaces,
    !isImmutable
      ? {
          trackedTime: {
            errorIconAriaLabel: 'State Validation Error',
            editingCell: (item, { currentValue, setValue }) =>
              shift ? (
                <Input
                  onChange={({ detail }) => {
                    // Strip out anything other than digits or :
                    const rawTime = (detail.value.match(/[\d:]/g) || []).join('');
                    setValue(rawTime);
                  }}
                  value={currentValue ?? parseTime('0:' + item.trackedTime, shiftMins).time}
                  placeholder="hh:mm"
                  ariaRequired
                  ariaLabel={t('coordinatorAttendeeTable.trackTime')}
                  inputMode="numeric"
                  step={1}
                />
              ) : null,
            validation: (item, value) => {
              const parsed = parseTime(value || '0:' + item.trackedTime, shiftMins);
              if (!parsed.error) return undefined;

              return t(parsed.error, { ns: 'shifts', hours: parsed.hours, min: parsed.min });
            },
            disabledReason: (item) => {
              if (item.alias === item.trackedBy && item.alias !== userId) {
                return t('coordinatorAttendeeTable.selfTracked');
              }

              if (item.attendance !== 'ATTENDED') {
                return t('coordinatorAttendeeTable.userDidNotAttend');
              }

              return undefined;
            },
          },
        }
      : undefined
  );

  const { updateParticipants } = useParticipantSessionStorage();
  const { updateBulkParticipants, updateBulkParticipantsFromParticipantData } =
    useBulkParticipantSessionStorage();

  const [modalVisible, setModalVisible] = useState(false);

  return (
    <>
      <Table
        {...props}
        header={
          <Header
            actions={
              props.isImmutable ? (
                <CopyToClipboard
                  copyButtonText={t('coordinatorAttendeeTable.copyAttendees')}
                  copyErrorText={t('coordinatorAttendeeTable.copyAttendeesFailed')}
                  copySuccessText={t('coordinatorAttendeeTable.copyAttendeesSuccess')}
                  data-aci-analytics-name="coordinatorAttendance_copyAliasList"
                  textToCopy={
                    selectedItems.length > 0
                      ? getAliases(selectedItems).join(';')
                      : getAliases(participants).join(';')
                  }
                />
              ) : (
                <SpaceBetween direction="horizontal" size="xs">
                  <Button
                    disabled={selectedItems && selectedItems.length === 0}
                    onClick={() => {
                      if (isEventLevelTracking) {
                        selectedItems.forEach((item) => {
                          // Event-level: use composite key 'alias-shiftId' to target specific shift
                          const key = `${item.alias}-${item.shiftId}`;
                          updateBulkParticipants({
                            [key]: {
                              ...item,
                              originalShiftId: item.originalShiftId || item.shiftId,
                              attendance: 'DID_NOT_ATTEND',
                              trackedTime: 0,
                            },
                          });
                        });
                      } else {
                        const aliases = getAliases(selectedItems);
                        const mixin: ParamsParticipantData = {};
                        aliases.forEach(
                          (alias) =>
                            (mixin[alias] = {
                              attendance: 'DID_NOT_ATTEND',
                              trackedTime: 0,
                            })
                        );
                        updateParticipants(mixin);
                        updateBulkParticipantsFromParticipantData(mixin);
                      }
                      publishKatalMetric(
                        'coordinatorAttendance_didNotAttend',
                        selectedItems.length.toString()
                      );
                    }}
                  >
                    {t('coordinatorAttendeeTable.removeAttendees')}
                  </Button>
                  <Button
                    variant="primary"
                    disabled={selectedItems.length === 0 || !shift}
                    onClick={() => {
                      const aliases = getAliases(selectedItems);
                      setModalVisible(true);
                      publishKatalMetric(
                        'coordinatorAttendance_openTrackTime',
                        aliases.length.toString()
                      );
                    }}
                  >
                    {t('coordinatorAttendeeTable.trackTime')}
                  </Button>
                </SpaceBetween>
              )
            }
            counter={
              selectedItems.length
                ? `(${selectedItems.length}/${participants.length})`
                : `(${participants.length})`
            }
          >
            {t('coordinatorAttendeeTable.title')}
          </Header>
        }
        filter={
          <TablePropertyFilter
            {...propertyFilterProps}
            propertyFilterStrings={propertyFilterStrings}
            filteringPlaceholder={t('coordinatorAttendeeTable.filteringPlaceholder')}
          />
        }
        pagination={<Pagination {...paginationProps} ariaLabels={paginationStrings} />}
        preferences={
          <PeoplePreferences
            onConfirm={({ detail }) => setPageSize(detail.pageSize || 100)}
            preferences={{
              pageSize,
            }}
          />
        }
        selectionType={props.isImmutable ? undefined : 'multi'}
        {...tableStrings}
        {...columns}
        {...collectionProps}
        // Do not allow modifications if the user tracked their own attendance
        isItemDisabled={(item) =>
          (item.alias === item.trackedBy && item.alias !== userId) || item.attendance !== 'ATTENDED'
        }
        submitEdit={(item, column, newVal) => {
          if (isEventLevelTracking) {
            // Event-level: use composite key 'alias-shiftId' to target specific shift
            // Calculate shift length for this specific item's shift
            const itemShift = props.shifts?.find((s) => s.shiftId === item.shiftId);
            const itemShiftMins = calculateShiftTime(itemShift, vcCreated);
            const key = `${item.alias}-${item.shiftId}`;
            updateBulkParticipants({
              [key]: {
                ...item,
                originalShiftId: item.originalShiftId || item.shiftId,
                attendance: 'ATTENDED',
                trackedTime: parseTime(newVal as string, itemShiftMins).minutesReported,
              },
            });
          } else {
            const mixin = {
              [item.alias || '']: {
                attendance: 'ATTENDED',
                trackedTime: parseTime(newVal as string, shiftMins).minutesReported,
              },
            };
            updateParticipants(mixin);
            updateBulkParticipantsFromParticipantData(mixin);
          }
        }}
        loading={isLoading}
        items={items}
        trackBy={isEventLevelTracking ? (item) => `${item.alias}-${item.shiftId}` : 'alias'}
        data-testid={'track-time-table'}
      />
      {!isLoading && isFetched && shift ? (
        <TimeModal
          shifts={props.shifts || [shift]}
          vcCreated={vcCreated}
          visible={modalVisible}
          onDismiss={() => {
            setModalVisible(false);
          }}
          selectedItems={selectedItems}
          isEventLevelTracking={isEventLevelTracking}
          saveToUrl
        />
      ) : null}
    </>
  );
}
